import { Injectable, inject, signal, computed } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import { ProjectInfo, ProductInfo, FinishOptionInfo, ThemeSetInfo, ThemeInfo, EditorType, DraftStorytellingData, DraftStorytellingChapter } from '../models';
import { AlertService } from './alert.service';
import { AppStateService } from './app-state.service';

/**
 * Calendar configuration passed from config dialog to editor.
 */
export interface CalendarEditorConfig {
  year: number;
  month: number;       // 0-indexed (0=Jan)
  startAtSunday: boolean;
  holidays: boolean;
  countryId: number | null;
  countryName: string | null;
  stateId: number | null; // UK sub-region; null for every other country
  orientation?: 'portrait' | 'landscape';
}

/**
 * Draft return context - persisted in URL query params so the editor
 * can navigate back to the products page with the draft pre-loaded.
 */
export interface DraftReturnContext {
  draftId: number;
  draftServerName: string;
  draftName: string;
  draftCategoryId: number;
  draftFavorite: boolean;
  restoredFromRefresh?: boolean;
}

/**
 * Spine info for bind=0 covers - available spine widths based on page count.
 */
export interface EditorSpineInfo {
  id: string;
  ref: string;
  width: number; // Spine width in cm
}

/**
 * Bleed (flap) dimensions for bind=0 covers.
 */
export interface EditorCoverBleed {
  top: number;    // Top flap height in cm
  bottom: number; // Bottom flap height in cm
  left: number;   // Left flap width in cm
  right: number;  // Right flap width in cm
}

/**
 * Layout data for bind=0 covers (full cover with flaps and spine).
 * This type of cover shows the complete spread: left flap + left front + spine + right front + right flap.
 */
export interface BindZeroCoverLayoutData {
  bind: string;           // "0" for this type
  frontWidth: number;     // Width of each front panel in cm
  frontHeight: number;    // Height of each front panel in cm
  spineGap: number;       // Gap for spine area in cm
  bleed: EditorCoverBleed;      // Flap dimensions
  spines: EditorSpineInfo[];    // Available spine widths (sorted by width)
  currentSpineWidth: number;    // Current spine width based on sheets
  sheetThickness: number;       // Thickness per sheet (from finish options)
}

/**
 * Type of project being edited - used to track which product section
 * the project belongs to when returning from editor.
 */
export type EditorProjectType = 'cover' | 'album_pages' | 'generic';

/**
 * Editor State Service
 *
 * Stores the current editor context and handles navigation to the editor route.
 * The actual project loading is handled by the editor component.
 */
@Injectable({
  providedIn: 'root'
})
export class EditorStateService {
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly alertService = inject(AlertService);
  private readonly appState = inject(AppStateService);

  /**
   * Editor focus mode: everything but the canvas and the editor's own toolbar
   * is hidden. Lives here, not in the editor component, because the app
   * topbar it also hides is rendered outside the editor's tree.
   * Transient — the editor clears it on destroy so the chrome can never stay
   * hidden on another route.
   */
  readonly focusMode = signal(false);

  private _projectInfo = signal<ProjectInfo | null>(null);
  private _project = signal<any | null>(null);
  private _projectModel = signal<any | null>(null);
  private _product = signal<ProductInfo | null>(null);
  private _loading = signal(false);
  private _error = signal<string | null>(null);
  // Selected finish values from product configuration (optionId -> finishId or value)
  private _selectedFinishValues = signal<Map<number, any>>(new Map());
  // Finish options loaded for the product
  private _finishOptions = signal<FinishOptionInfo[]>([]);
  // Bind=0 cover layout data for spread view (flaps, spine, page dimensions)
  private _bindZeroCoverLayout = signal<BindZeroCoverLayoutData | null>(null);
  // Cover preview DPI for correct positioning of page window on cover background
  // This is read from the actual image metadata in the album form
  private _coverPreviewDpi = signal<number | null>(null);

  // Pre-defined project name from order/album form
  private _preDefinedProjectName = signal<string | null>(null);
  // Existing project ID when editing an existing project
  private _existingProjectId = signal<number | null>(null);
  // Existing project serverName when editing
  private _existingProjectServerName = signal<string | null>(null);

  // Project type being edited - used to track which section the project belongs to
  private _currentProjectType = signal<EditorProjectType>('generic');

  // Calendar configuration from config dialog (set before editor launch)
  private _calendarConfig = signal<CalendarEditorConfig | null>(null);

  // Saved project info - updated by editor after save, read by album form on return
  private _savedProjectId = signal<number | null>(null);
  private _savedProjectServerName = signal<string | null>(null);
  private _savedProjectServerId = signal<number | null>(null);
  private _savedProjectPathStructureId = signal<number | null>(null);
  private _savedProjectType = signal<EditorProjectType>('generic');
  // Product the saved project belongs to. Populated from the editor's current
  // product when setSavedProject runs; read by checkForSavedProject to reject
  // cross-product leaks (e.g. finishing a mug project and then opening a
  // canvas — the mug's saved entry must not get auto-applied to the canvas).
  private _savedProductId = signal<number | null>(null);
  // Page thumbnails for album pages project (array of data URLs or S3 URLs)
  private _savedPageThumbnails = signal<string[]>([]);
  // Total page count for album pages project (may differ from thumbnails if not all pages were visited)
  private _savedPageCount = signal<number>(0);
  // High-quality preview URLs for album pages (S3 URLs uploaded on save)
  private _savedPreviewUrls = signal<{
    coverFront: string;
    coverBack: string;
    spreads: string[];
    generatedAt: string;
  } | null>(null);
  // Transparent thumbnail for cover projects (allows finish color to show through)
  private _savedProjectTransparentThumbnail = signal<string | null>(null);
  // Page position percentages for transparent thumbnail (matches crop coordinates)
  private _savedProjectThumbnailPosition = signal<{
    left: number;
    top: number;
    width: number;
    height: number;
    /** Print-bleed fractions of the captured raster (standard cover
     *  projects). The thumb KEEPS the bleed — consumers split it into the
     *  TRIM face + the fold bands using these fractions. */
    bleed?: { left: number; right: number; top: number; bottom: number };
  } | null>(null);
  // Panel name for generic projects (to track which panel the project belongs to)
  private _currentPanelName = signal<string | null>(null);
  private _savedPanelName = signal<string | null>(null);
  // Storytelling data - saved by editor after pagination, persisted in draft
  private _savedStorytellingData = signal<DraftStorytellingData | null>(null);
  // Storytelling data restored from draft (products-list → editor on page load)
  private _restoredStorytellingData = signal<DraftStorytellingData | null>(null);
  // Storytelling WIP (work-in-progress) chapters saved but not yet applied to album
  private _savedStorytellingWip = signal<DraftStorytellingChapter[] | null>(null);
  private _restoredStorytellingWip = signal<DraftStorytellingChapter[] | null>(null);
  // Cover URLs for preview generation (passed from products-list to editor)
  private _coverUrlsForPreview = signal<{ front: string; back: string } | null>(null);

  // Guarda (endpaper) image URL for preview generation - small texture to be tiled
  private _guardaImageUrl = signal<string | null>(null);

  // Transparent sheet (folha papel vegetal) page thumbnail for preview generation
  private _transparentSheetPageUrl = signal<string | null>(null);

  // Transparent sheet editor capture mode - when true, editor captures snapshot and auto-closes
  private _transparentSheetCaptureMode = signal(false);

  // Captured transparent sheet snapshot (HQ snapshot from editor with fonts loaded)
  private _capturedTransparentSheetSnapshot = signal<string | null>(null);

  // Transparent sheet panel info for navigation back to album after capture
  private _transparentSheetAlbumContext = signal<{
    albumProjectId: number;
    albumServerName: string;
    coverUrls?: { front: string; back: string };
    guardaImageUrl?: string;
  } | null>(null);

  // Bind=0 cover capture mode - for high-quality cover snapshots without flaps/spine
  private _bindZeroCoverCaptureMode = signal(false);

  // Captured bind=0 cover snapshots (front and back halves, cropped without flaps/spine)
  private _capturedBindZeroCoverSnapshots = signal<{
    front: string;  // Right half of spread (without right flap)
    back: string;   // Left half of spread (without left flap)
  } | null>(null);

  // Bind=0 cover capture context - layout info for cropping flaps/spine
  private _bindZeroCoverCaptureContext = signal<{
    layout: {
      flapTopPercent: number;
      flapBottomPercent: number;
      flapLeftPercent: number;
      flapRightPercent: number;
      spineWidthPercent: number;
      pageWidthPercent: number;
    };
    returnToFlipbook: boolean;
    albumProjectId?: number;
    albumServerName?: string;
  } | null>(null);

  // Standard cover (non-bind=0) capture mode - for high-quality cover snapshots with bleed cropping
  private _standardCoverCaptureMode = signal(false);

  // Standard cover capture context - tracks front/back and album context
  private _standardCoverCaptureContext = signal<{
    isFrontCover: boolean;       // true = capturing front, false = capturing back
    returnToFlipbook: boolean;
    albumProjectId?: number;
    albumServerName?: string;
    finishImageUrl?: string;     // Finish/Revestimento image to apply as background before capture
    overlayImageUrls?: string[]; // Overlay images (e.g., lomba) to apply on top
  } | null>(null);

  // Album disposition: 0 = single pages, 1 = double-page spreads (default)
  private _albumDisposition = signal<number>(1);

  // Initial page photos for auto-population when converting files to album project
  // Array of photoInfo objects (with sizes, serverName, etc.) from uploaded files
  private _initialPageImages = signal<any[]>([]);

  // When true, the editor should navigate through all pages after load to refresh thumbnails
  private _refreshAllThumbnails = signal(false);

  // Conversion progress dialog reference (for updating progress from editor)
  private _conversionProgressDialog = signal<any>(null);

  // Return URL for navigating back after preview generation
  private _previewReturnUrl = signal<string | null>(null);

  // Draft return context for navigating back to products page with draft pre-loaded
  private _draftReturnContext = signal<DraftReturnContext | null>(null);

  // Album Design context — set by the Album Design board before launching the
  // editor so the editor tags its saves as draft_kind=1 (create/update via the
  // album-design upload route). One-shot: consumed at the next editor navigation.
  // albumProductId / albumFormatId (= the picked album product id and its
  // format-family subTypeId) are carried so the saved draft can later graduate
  // straight into the album config form (see restoreAlbumConfig).
  private _albumDesignContext = signal<{
    draftId: number | null;
    albumProductId?: number;
    albumFormatId?: number;
  } | null>(null);

  // Selected theme for new project - set when user selects a theme from the theme gallery dialog
  private _selectedTheme = signal<ThemeSetInfo | null>(null);
  private _selectedThemeVariant = signal<ThemeInfo | null>(null);

  /**
   * Flipbook capture chain state - tracks all capture steps for preview generation.
   * This ensures proper chaining: standard cover → bind=0 cover → transparent sheet → album editor → open flipbook.
   */
  private _flipbookCaptureChain = signal<{
    active: boolean;                     // Whether a capture chain is in progress
    returnUrl: string;                   // URL to return to after all captures complete
    // Step 0: Standard cover capture (non-bind=0) - captures front then back
    needsStandardCoverCapture: boolean;
    standardCoverProjectId?: number;
    standardCoverServerName?: string;
    standardCoverProductId?: number;      // Cover product ID for loadProductConfig
    standardCoverEditorTypeId?: number;   // Editor type (COVERS=3) for finish priority
    standardCoverFinishImageUrl?: string; // Finish/Revestimento image to use as background
    standardCoverOverlayImageUrls?: string[]; // Additional overlay images (e.g., lomba) to apply on top
    standardCoverCapturedFront?: boolean; // Track if front is captured (need to capture back next)
    // Step 1: Bind=0 cover capture
    needsBindZeroCoverCapture: boolean;
    bindZeroCoverProjectId?: number;
    bindZeroCoverServerName?: string;
    bindZeroCoverLayout?: {
      flapTopPercent: number;
      flapBottomPercent: number;
      flapLeftPercent: number;
      flapRightPercent: number;
      spineWidthPercent: number;
      pageWidthPercent: number;
    };
    // Bind=0 cover compositing data (revestimento + position for compositing in editor before S3 upload)
    bindZeroCoverRevestimentoFrontUrl?: string;
    bindZeroCoverRevestimentoBackUrl?: string;
    bindZeroCoverDesignPosition?: { left: number; top: number; width: number; height: number };
    // Step 2: Transparent sheet capture
    needsTransparentSheetCapture: boolean;
    transparentSheetProjectId?: number;
    transparentSheetServerName?: string;
    // Step 3: Album editor for page previews
    albumProjectId: number;
    albumServerName: string;
    coverUrls?: { front: string; back: string };
    guardaImageUrl?: string;
  } | null>(null);

  /**
   * Capture chain loading state - shows loading overlay during entire capture chain.
   * Contains the current step message to display to the user.
   */
  private _captureChainLoading = signal<{
    active: boolean;
    message: string;
    currentStep: 'cover' | 'transparent_sheet' | 'book_block' | 'uploading' | 'done';
    progress?: { current: number; total: number };
  } | null>(null);

  // Editor opening state - shows global overlay while editor lazy-loads
  private _editorOpening = signal(false);

  readonly projectInfo = this._projectInfo.asReadonly();
  readonly project = this._project.asReadonly();
  readonly projectModel = this._projectModel.asReadonly();
  readonly product = this._product.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly selectedFinishValues = this._selectedFinishValues.asReadonly();
  readonly finishOptions = this._finishOptions.asReadonly();
  readonly bindZeroCoverLayout = this._bindZeroCoverLayout.asReadonly();
  readonly coverPreviewDpi = this._coverPreviewDpi.asReadonly();

  // Pre-defined project name and existing project info
  readonly preDefinedProjectName = this._preDefinedProjectName.asReadonly();
  readonly existingProjectId = this._existingProjectId.asReadonly();
  readonly existingProjectServerName = this._existingProjectServerName.asReadonly();

  // Current project type being edited
  readonly currentProjectType = this._currentProjectType.asReadonly();

  // Calendar editor configuration
  readonly calendarConfig = this._calendarConfig.asReadonly();

  setCalendarConfig(config: CalendarEditorConfig | null): void {
    this._calendarConfig.set(config);
  }

  // Saved project info for album form to read on return
  readonly savedProjectId = this._savedProjectId.asReadonly();
  readonly savedProjectServerName = this._savedProjectServerName.asReadonly();
  readonly savedProjectServerId = this._savedProjectServerId.asReadonly();
  readonly savedProjectPathStructureId = this._savedProjectPathStructureId.asReadonly();
  readonly savedProjectType = this._savedProjectType.asReadonly();
  readonly savedProductId = this._savedProductId.asReadonly();
  readonly savedPageThumbnails = this._savedPageThumbnails.asReadonly();
  readonly savedPageCount = this._savedPageCount.asReadonly();
  readonly savedPreviewUrls = this._savedPreviewUrls.asReadonly();
  readonly savedProjectTransparentThumbnail = this._savedProjectTransparentThumbnail.asReadonly();
  readonly savedProjectThumbnailPosition = this._savedProjectThumbnailPosition.asReadonly();
  readonly currentPanelName = this._currentPanelName.asReadonly();
  readonly savedPanelName = this._savedPanelName.asReadonly();
  readonly savedStorytellingData = this._savedStorytellingData.asReadonly();
  readonly restoredStorytellingData = this._restoredStorytellingData.asReadonly();
  readonly savedStorytellingWip = this._savedStorytellingWip.asReadonly();
  readonly restoredStorytellingWip = this._restoredStorytellingWip.asReadonly();
  readonly coverUrlsForPreview = this._coverUrlsForPreview.asReadonly();
  readonly guardaImageUrl = this._guardaImageUrl.asReadonly();
  readonly transparentSheetPageUrl = this._transparentSheetPageUrl.asReadonly();
  readonly transparentSheetCaptureMode = this._transparentSheetCaptureMode.asReadonly();
  readonly capturedTransparentSheetSnapshot = this._capturedTransparentSheetSnapshot.asReadonly();
  readonly transparentSheetAlbumContext = this._transparentSheetAlbumContext.asReadonly();
  readonly bindZeroCoverCaptureMode = this._bindZeroCoverCaptureMode.asReadonly();
  readonly capturedBindZeroCoverSnapshots = this._capturedBindZeroCoverSnapshots.asReadonly();
  readonly bindZeroCoverCaptureContext = this._bindZeroCoverCaptureContext.asReadonly();
  readonly standardCoverCaptureMode = this._standardCoverCaptureMode.asReadonly();
  readonly standardCoverCaptureContext = this._standardCoverCaptureContext.asReadonly();
  readonly albumDisposition = this._albumDisposition.asReadonly();
  readonly initialPageImages = this._initialPageImages.asReadonly();
  readonly refreshAllThumbnails = this._refreshAllThumbnails.asReadonly();
  readonly conversionProgressDialog = this._conversionProgressDialog.asReadonly();
  readonly previewReturnUrl = this._previewReturnUrl.asReadonly();
  readonly draftReturnContext = this._draftReturnContext.asReadonly();
  readonly albumDesignContext = this._albumDesignContext.asReadonly();
  readonly flipbookCaptureChain = this._flipbookCaptureChain.asReadonly();
  readonly captureChainLoading = this._captureChainLoading.asReadonly();
  readonly editorOpening = this._editorOpening.asReadonly();

  /** Get bind=0 cover compositing data (revestimento URLs + position) for S3 preview generation */
  getBindZeroCoverCompositingData(): {
    revestimentoFrontUrl?: string;
    revestimentoBackUrl?: string;
    designPosition?: { left: number; top: number; width: number; height: number };
  } | null {
    const chain = this._flipbookCaptureChain();
    if (!chain) return null;
    if (!chain.bindZeroCoverRevestimentoFrontUrl && !chain.bindZeroCoverRevestimentoBackUrl) return null;
    return {
      revestimentoFrontUrl: chain.bindZeroCoverRevestimentoFrontUrl,
      revestimentoBackUrl: chain.bindZeroCoverRevestimentoBackUrl,
      designPosition: chain.bindZeroCoverDesignPosition
    };
  }

  // Selected theme for new project
  readonly selectedTheme = this._selectedTheme.asReadonly();
  readonly selectedThemeVariant = this._selectedThemeVariant.asReadonly();

  readonly hasProject = computed(() => this._projectInfo() !== null);
  readonly projectName = computed(() => this._projectInfo()?.name || '');

  /**
   * Store project info and navigate to editor for loading.
   */
  async loadProject(projectInfo: ProjectInfo): Promise<boolean> {
    console.log('[EditorStateService] Loading project:', projectInfo);
    this._loading.set(true);
    this._error.set(null);
    // Clear preview URLs from previous project
    this._savedPreviewUrls.set(null);

    try {
      this._projectInfo.set(projectInfo);
      this._project.set(null);
      this._projectModel.set(null);

      // Set project metadata so editor uses the legacy S3 loading path
      // (the Dreambooks v3 API path requires a different auth token)
      if (projectInfo.serverName) {
        this._existingProjectServerName.set(projectInfo.serverName);
      }
      if (projectInfo.id) {
        this._existingProjectId.set(projectInfo.id);
      }
      // Store serverId and pathStructureId for the save flow. The previous
      // truthy check (`if (projectInfo.serverId)`) silently dropped the
      // value when it was 0 or undefined — and downstream save flows then
      // persisted `coverProjectServerId: undefined` to the draft, which
      // later triggered PathService's "Server 0 not found" warnings on
      // re-open. Log explicitly when the project arrives without a real
      // serverId so we can audit the upstream API / save path. Use
      // `?? null` so 0 is preserved as a real value (CMS server ID),
      // not silently dropped.
      if (projectInfo.serverId != null) {
        this._savedProjectServerId.set(projectInfo.serverId);
      } else {
        console.warn('[EditorStateService] loadProject received project without serverId:', {
          projectId: projectInfo.id,
          serverName: projectInfo.serverName
        });
      }
      if (projectInfo.pathStructureId != null) {
        this._savedProjectPathStructureId.set(projectInfo.pathStructureId);
      }

      // Set project name so editor preserves it during save
      if (projectInfo.name) {
        this._preDefinedProjectName.set(projectInfo.name);
      }

      // Infer project type from editorTypeId so save flow routes correctly
      // Use Number() to handle string values from AMF deserialization
      const editorTypeId = Number(projectInfo.editorTypeId);
      console.log('[EditorStateService] loadProject editorTypeId:', projectInfo.editorTypeId, '→ Number:', editorTypeId, 'ALBUNS:', EditorType.ALBUNS);
      if (editorTypeId === EditorType.ALBUNS) {
        this._currentProjectType.set('album_pages');
      } else if (editorTypeId === EditorType.COVERS) {
        this._currentProjectType.set('cover');
      } else {
        this._currentProjectType.set('generic');
      }
      console.log('[EditorStateService] loadProject currentProjectType set to:', this._currentProjectType());

      // Panel name unknown when coming from projects panel
      this._currentPanelName.set(null);

      await this.navigateToEditor(projectInfo.id);
      return true;
    } catch (err: any) {
      console.error('[EditorStateService] Error loading project:', err);
      this._error.set(this.translate.instant('UI_TEXT.PROJECT_OPEN_ERROR'));
      this.alertService.error(this.translate.instant('UI_TEXT.PROJECT_OPEN_ERROR'));
      return false;
    } finally {
      this._loading.set(false);
    }
  }

  /**
   * Create a new project and navigate to editor.
   * @param productId - The product ID to create a project for
   * @param productInfo - Optional product info with details
   * @param finishValues - Optional map of selected finish values (optionId -> finishId)
   * @param finishOptions - Optional array of finish options with available finishes
   * @param bindZeroLayout - Optional bind=0 cover layout data for spread view
   * @param previewDpi - Optional cover preview image DPI for correct page positioning
   * @param projectName - Optional pre-defined project name from order
   * @param existingProjectId - Optional existing project ID to edit instead of creating new
   * @param existingProjectServerName - Optional serverName for existing project
   * @param projectType - Type of project being edited (cover, album_pages, generic)
   * @param panelName - Optional panel name for generic projects (to track which panel the project belongs to)
   */
  /**
   * Reset all transient project state to defaults.
   * Called at the top of createNewProject() as a guardrail against state leaks
   * between products — even if closeProject() misses a signal, this ensures
   * a clean slate before any new values are set.
   */
  private resetForNewProject(): void {
    this._projectInfo.set(null);
    this._project.set(null);
    this._projectModel.set(null);
    this._product.set(null);
    this._error.set(null);
    this._selectedFinishValues.set(new Map());
    this._finishOptions.set([]);
    this._bindZeroCoverLayout.set(null);
    this._coverPreviewDpi.set(null);
    this._preDefinedProjectName.set(null);
    this._existingProjectId.set(null);
    this._existingProjectServerName.set(null);
    // NOTE: do NOT clear _calendarConfig here. The calendar flow calls
    // setCalendarConfig() right before createNewProject() (which runs this
    // reset at the top), so wiping it would drop the dialog selections the
    // user just made. closeProject() handles the cross-session cleanup.
    this._currentProjectType.set('generic');
    this._currentPanelName.set(null);
    this._restoredStorytellingData.set(null);
    this._restoredStorytellingWip.set(null);
  }

  async createNewProject(
    productId: number,
    productInfo?: ProductInfo,
    finishValues?: Map<number, any>,
    finishOptions?: FinishOptionInfo[],
    bindZeroLayout?: BindZeroCoverLayoutData | null,
    previewDpi?: number | null,
    projectName?: string | null,
    existingProjectId?: number | null,
    existingProjectServerName?: string | null,
    projectType: EditorProjectType = 'generic',
    panelName?: string | null
  ): Promise<boolean> {
    console.log('[EditorStateService] Creating new project for product:', productId);
    console.log('[EditorStateService] Finish values:', finishValues);
    console.log('[EditorStateService] Finish options:', finishOptions);
    console.log('[EditorStateService] Bind=0 layout:', bindZeroLayout);
    console.log('[EditorStateService] Preview DPI:', previewDpi);
    console.log('[EditorStateService] Pre-defined project name:', projectName);
    console.log('[EditorStateService] Existing project ID:', existingProjectId);
    console.log('[EditorStateService] Project type:', projectType);
    console.log('[EditorStateService] Panel name:', panelName);

    // Guardrail: ensure clean slate before setting new project state
    this.resetForNewProject();

    this._loading.set(true);
    this._error.set(null);
    this._editorOpening.set(true);

    try {
      if (productInfo) {
        this._product.set(productInfo);
      }
      if (finishValues) {
        this._selectedFinishValues.set(finishValues);
      }
      if (finishOptions) {
        this._finishOptions.set(finishOptions);
      }
      // Set bind=0 layout (can be null to clear it)
      this._bindZeroCoverLayout.set(bindZeroLayout || null);
      // Set cover preview DPI for correct page positioning
      this._coverPreviewDpi.set(previewDpi ?? null);
      // Set pre-defined project name (from order)
      this._preDefinedProjectName.set(projectName || null);
      // Set existing project info (for editing)
      this._existingProjectId.set(existingProjectId || null);
      this._existingProjectServerName.set(existingProjectServerName || null);
      // Set project type for tracking which section this project belongs to
      this._currentProjectType.set(projectType);
      // Set panel name for generic projects
      this._currentPanelName.set(panelName || null);

      // Compute effective finishId for URL persistence (refresh resilience)
      const editorTypeId = Number(productInfo?.editorTypeId ?? EditorType.GIFTS);
      const backgroundFinishId = this.getBackgroundFinishId();
      const overlayFinishes = this.getOverlayFinishes();
      const overlayFinishId = overlayFinishes.length > 0 ? overlayFinishes[0].finishId : 0;
      let effectiveFinishId: number;
      if (editorTypeId === EditorType.COVERS) {
        effectiveFinishId = backgroundFinishId > 0 ? backgroundFinishId : overlayFinishId;
      } else {
        effectiveFinishId = overlayFinishId > 0 ? overlayFinishId : backgroundFinishId;
      }
      const finishParam = effectiveFinishId > 0 ? { finishId: effectiveFinishId } : {};
      const editorTypeParam = { editorTypeId };

      // Build project type/panel params for URL persistence (refresh resilience)
      const projectTypeParam = projectType !== 'generic' ? { projectType } : {};
      const panelNameParam = panelName ? { panelName } : {};

      // Build draft params if draft context is set (for refresh resilience).
      // serverName / name / favorite are no longer mirrored — the editor
      // re-fetches them via `getDraftMeta(draftId)` on refresh.
      const draftCtx = this._draftReturnContext();
      const draftParams = draftCtx ? {
        draftId: draftCtx.draftId,
        draftCategoryId: draftCtx.draftCategoryId,
      } : {};

      const albumDesignParams = this.albumDesignParams();

      // If editing existing project, navigate with projectId instead of newProject
      if (existingProjectId) {
        await this.router.navigate(this.appState.buildLocaleCommands(['editor']), {
          queryParams: {
            projectId: existingProjectId,
            ...editorTypeParam,
            ...finishParam,
            ...projectTypeParam,
            ...panelNameParam,
            ...draftParams,
            ...albumDesignParams
          }
        });
      } else {
        await this.router.navigate(this.appState.buildLocaleCommands(['editor']), {
          queryParams: {
            productId,
            newProject: true,
            ...editorTypeParam,
            ...finishParam,
            ...projectTypeParam,
            ...panelNameParam,
            ...draftParams,
            ...albumDesignParams
          }
        });
      }

      // One-shot context: consumed by this navigation.
      this._albumDesignContext.set(null);
      return true;
    } catch (err: any) {
      console.error('[EditorStateService] Error creating project:', err);
      this._error.set(this.translate.instant('UI_TEXT.PROJECT_CREATE_ERROR'));
      return false;
    } finally {
      this._loading.set(false);
    }
  }

  setProjectState(projectModel: any | null, project: any | null): void {
    this._projectModel.set(projectModel);
    this._project.set(project);
  }

  /**
   * Called by the editor after successfully saving a project.
   * Stores project info so the album form can read it on return.
   * @param projectId - The saved project ID
   * @param serverName - The server name for the project files
   * @param serverId - Optional server ID
   * @param pathStructureId - Optional path structure ID
   * @param pageThumbnails - Optional array of page thumbnails (for album pages)
   * @param transparentThumbnail - Optional transparent thumbnail for cover projects (allows finish color to show through)
   * @param thumbnailPosition - Optional page position percentages for transparent thumbnail positioning
   * @param pageCount - Optional total page count (for albums, may differ from thumbnail count if not all pages were visited)
   */
  setSavedProject(
    projectId: number,
    serverName: string,
    serverId?: number,
    pathStructureId?: number,
    pageThumbnails?: string[],
    transparentThumbnail?: string,
    thumbnailPosition?: { left: number; top: number; width: number; height: number; bleed?: { left: number; right: number; top: number; bottom: number } },
    pageCount?: number
  ): void {
    const projectType = this._currentProjectType();
    const panelName = this._currentPanelName();
    const productId = Number(this._product()?.id) || null;
    console.log('[EditorStateService] Setting saved project:', { projectId, serverName, serverId, pathStructureId, projectType, panelName, productId, pageThumbnails: pageThumbnails?.length, pageCount, hasTransparentThumbnail: !!transparentThumbnail, thumbnailPosition });
    this._savedProjectId.set(projectId);
    this._savedProjectServerName.set(serverName);
    this._savedProjectServerId.set(serverId ?? null);
    this._savedProjectPathStructureId.set(pathStructureId ?? null);
    this._savedProjectType.set(projectType);
    this._savedPanelName.set(panelName);
    this._savedProductId.set(productId);
    this._savedPageThumbnails.set(pageThumbnails || []);
    this._savedPageCount.set(pageCount ?? pageThumbnails?.length ?? 0);
    this._savedProjectTransparentThumbnail.set(transparentThumbnail || null);
    this._savedProjectThumbnailPosition.set(thumbnailPosition || null);
  }

  /**
   * Clear saved project info after album form has read it.
   */
  clearSavedProject(): void {
    console.log('[EditorStateService] Clearing saved project');
    this._savedProjectId.set(null);
    this._savedProjectServerName.set(null);
    this._savedProjectServerId.set(null);
    this._savedProjectPathStructureId.set(null);
    this._savedProjectType.set('generic');
    this._savedPanelName.set(null);
    this._savedProductId.set(null);
    this._savedPageThumbnails.set([]);
    this._savedPageCount.set(0);
    // Note: Do NOT clear _savedPreviewUrls here - it's needed for flipbook preview
    this._savedProjectTransparentThumbnail.set(null);
    this._savedProjectThumbnailPosition.set(null);
  }

  /**
   * Set the selected theme for a new project.
   * Called when user selects a theme from the theme gallery dialog.
   * The editor will read this and load the theme project.
   *
   * @param theme - The selected theme set
   * @param variant - The specific theme variant (color/locale)
   */
  setSelectedTheme(theme: ThemeSetInfo | null, variant?: ThemeInfo | null): void {
    console.log('[EditorStateService] Setting selected theme:', theme?.name || theme?.ref, variant?.locale);
    this._selectedTheme.set(theme);
    this._selectedThemeVariant.set(variant || null);
  }

  /**
   * Clear the selected theme.
   * Called after the editor has loaded the theme.
   */
  clearSelectedTheme(): void {
    console.log('[EditorStateService] Clearing selected theme');
    this._selectedTheme.set(null);
    this._selectedThemeVariant.set(null);
  }

  /**
   * Set high-quality preview URLs for album pages.
   * Called by editor after uploading preview images to S3.
   * @param urls - The preview URLs object with spreads array
   */
  setPreviewUrls(urls: {
    coverFront: string;
    coverBack: string;
    spreads: string[];
    generatedAt: string;
  } | null): void {
    console.log('[EditorStateService] Setting preview URLs:', urls ? {
      coverFront: urls.coverFront ? 'set' : 'empty',
      coverBack: urls.coverBack ? 'set' : 'empty',
      spreads: urls.spreads.length,
      generatedAt: urls.generatedAt
    } : null);
    this._savedPreviewUrls.set(urls);
  }

  /**
   * Clear preview URLs (e.g., when opening a different project).
   */
  clearPreviewUrls(): void {
    console.log('[EditorStateService] Clearing preview URLs');
    this._savedPreviewUrls.set(null);
  }

  /**
   * Clear all preview-related caches to force fresh generation.
   * Call this at the start of preview generation to ensure no cached data is used.
   */
  clearAllPreviewCaches(): void {
    console.log('[EditorStateService] Clearing all preview caches for fresh generation');
    this._savedPreviewUrls.set(null);
    this._capturedBindZeroCoverSnapshots.set(null);
    this._capturedTransparentSheetSnapshot.set(null);
    this._flipbookCaptureChain.set(null);
  }

  /**
   * Check if there's a saved project waiting to be picked up by the album form.
   */
  hasSavedProject(): boolean {
    return this._savedProjectId() !== null;
  }

  // --- Storytelling persistence ---

  setStorytellingData(data: DraftStorytellingData): void {
    console.log('[EditorStateService] Setting storytelling data:', data.chapters.length, 'chapters');
    this._savedStorytellingData.set(data);
  }

  clearStorytellingData(): void {
    this._savedStorytellingData.set(null);
  }

  setRestoredStorytellingData(data: DraftStorytellingData): void {
    console.log('[EditorStateService] Setting restored storytelling data:', data.chapters.length, 'chapters');
    this._restoredStorytellingData.set(data);
  }

  clearRestoredStorytellingData(): void {
    this._restoredStorytellingData.set(null);
  }

  setStorytellingWip(chapters: DraftStorytellingChapter[]): void {
    console.log('[EditorStateService] Setting storytelling WIP:', chapters.length, 'chapters');
    this._savedStorytellingWip.set(chapters);
  }

  clearStorytellingWip(): void {
    this._savedStorytellingWip.set(null);
  }

  setRestoredStorytellingWip(chapters: DraftStorytellingChapter[]): void {
    console.log('[EditorStateService] Setting restored storytelling WIP:', chapters.length, 'chapters');
    this._restoredStorytellingWip.set(chapters);
  }

  clearRestoredStorytellingWip(): void {
    this._restoredStorytellingWip.set(null);
  }

  setDraftReturnContext(ctx: DraftReturnContext): void {
    this._draftReturnContext.set(ctx);
  }

  clearDraftReturnContext(): void {
    this._draftReturnContext.set(null);
  }

  /** Set/clear the Album Design launch context (see `_albumDesignContext`). */
  setAlbumDesignContext(ctx: { draftId: number | null; albumProductId?: number; albumFormatId?: number } | null): void {
    this._albumDesignContext.set(ctx);
  }

  clearAlbumDesignContext(): void {
    this._albumDesignContext.set(null);
  }

  /** Restore the pre-defined project name — e.g. an album-design draft name after a
   *  page refresh, where it is not otherwise re-derived from the loaded project. */
  setPreDefinedProjectName(name: string | null): void {
    this._preDefinedProjectName.set(name || null);
  }

  /** Query params tagging an editor launch as an Album Design (draft_kind=1) flow. */
  private albumDesignParams(): Record<string, number> {
    const ctx = this._albumDesignContext();
    if (!ctx) return {};
    const params: Record<string, number> = { albumDesign: 1 };
    if (ctx.draftId) params['albumDesignDraftId'] = ctx.draftId;
    if (ctx.albumProductId) params['albumProductId'] = ctx.albumProductId;
    if (ctx.albumFormatId) params['albumFormatId'] = ctx.albumFormatId;
    return params;
  }

  setCurrentProjectType(type: EditorProjectType): void {
    this._currentProjectType.set(type);
  }

  /**
   * Seed (or clear) the bind=0 cover layout outside the createNewProject flow.
   * Used by the theme editor, which derives the layout from getProduct itself
   * instead of receiving it from the album form.
   */
  setBindZeroCoverLayout(layout: BindZeroCoverLayoutData | null): void {
    this._bindZeroCoverLayout.set(layout);
  }

  setCurrentPanelName(name: string | null): void {
    this._currentPanelName.set(name);
  }

  closeProject(): void {
    console.log('[EditorStateService] closeProject called');

    this._projectInfo.set(null);
    this._project.set(null);
    this._projectModel.set(null);
    this._product.set(null);
    this._error.set(null);

    // IMPORTANT: Preserve finish values during capture chain
    // The next capture step may need these values to display correctly
    const isInCaptureChain = this.hasActiveCaptureChain();
    if (isInCaptureChain) {
      console.log('[EditorStateService] Preserving finish values during capture chain');
    } else {
      console.log('[EditorStateService] Clearing finish values (not in capture chain)');
      this._selectedFinishValues.set(new Map());
      this._finishOptions.set([]);
    }

    this._bindZeroCoverLayout.set(null);
    this._coverPreviewDpi.set(null);
    this._preDefinedProjectName.set(null);
    this._existingProjectId.set(null);
    this._existingProjectServerName.set(null);
    this._calendarConfig.set(null);
    this._currentProjectType.set('generic');
    this._currentPanelName.set(null);

    // Clear restored storytelling (editor-side only, no longer needed)
    this._restoredStorytellingData.set(null);
    this._restoredStorytellingWip.set(null);
    // Note: Don't clear _savedStorytellingData / _savedStorytellingWip here —
    // album form needs to read them in checkForSavedProject(), same as saved project info.
    // Leak prevention is handled by clearing local signals in restoreAlbumConfig().

    // Note: Don't clear saved project info here - album form needs to read it
  }

  /**
   * Set finish configuration for capture mode.
   * This allows the editor to display correctly when opened in capture mode,
   * using the same finish values as when opened normally.
   */
  setFinishConfigForCapture(
    finishValues: Map<number, any>,
    finishOptions: FinishOptionInfo[]
  ): void {
    console.log('[EditorStateService] Setting finish config for capture:', {
      finishValuesSize: finishValues.size,
      finishOptionsCount: finishOptions.length
    });
    this._selectedFinishValues.set(finishValues);
    this._finishOptions.set(finishOptions);
  }

  /**
   * Update the bind=0 cover spine width.
   * Called when sheet count or thickness changes in the album form.
   */
  updateBindZeroSpineWidth(newSpineWidth: number): void {
    const layout = this._bindZeroCoverLayout();
    if (!layout) return;

    this._bindZeroCoverLayout.set({
      ...layout,
      currentSpineWidth: newSpineWidth
    });
    console.log('[EditorStateService] Updated bind=0 spine width:', newSpineWidth);
  }

  /**
   * Get the selected finish ID for background-type options (like Revestimento/Coating).
   * Background finishes are options with type=7 (TABBED_DROPDOWN_IMAGES).
   * When passed to getProduct API, this finishId returns the correct background image.
   * @returns The finishId for the background finish, or 0 if not found.
   */
  getBackgroundFinishId(): number {
    const finishValues = this._selectedFinishValues();
    const finishOptions = this._finishOptions();

    console.log('[EditorStateService] getBackgroundFinishId - finishValues:', finishValues);
    console.log('[EditorStateService] getBackgroundFinishId - finishOptions count:', finishOptions.length);

    // Look for options with type=7 (TABBED_DROPDOWN_IMAGES) which are Revestimento/Coating type
    for (const option of finishOptions) {
      // Type 7 = TABBED_DROPDOWN_IMAGES (Revestimento/Coating finishes)
      if (option.type === 7) {
        const selectedFinishId = finishValues.get(option.id);
        console.log('[EditorStateService] Found type 7 option', option.id, '- selectedFinishId:', selectedFinishId);
        if (typeof selectedFinishId === 'number' && selectedFinishId > 0) {
          return selectedFinishId;
        }
      }
    }

    console.log('[EditorStateService] No background finish found');
    return 0;
  }

  /**
   * Get selected finishes that are overlay type and should provide overlay images.
   * Overlay finishes are identified by:
   * - type=4, OR
   * - _explicitType containing "OverlayFinishInfo"
   * These finishes provide overlay images to display on top of the editor canvas.
   */
  getOverlayFinishes(): { optionId: number; finishId: number; image: any }[] {
    const overlayFinishes: { optionId: number; finishId: number; image: any }[] = [];
    const finishValues = this._selectedFinishValues();
    const finishOptions = this._finishOptions();

    console.log('[EditorStateService] getOverlayFinishes - finishValues:', finishValues);
    console.log('[EditorStateService] getOverlayFinishes - finishOptions count:', finishOptions.length);

    for (const option of finishOptions) {
      const selectedFinishId = finishValues.get(option.id);
      console.log('[EditorStateService] Option', option.id, '- selectedFinishId:', selectedFinishId);
      if (selectedFinishId === undefined || selectedFinishId === null) continue;

      // Find the selected finish in this option
      const selectedFinish = option.finishes.find(f => f.id === selectedFinishId);
      console.log('[EditorStateService] Found selectedFinish:', selectedFinish);
      if (!selectedFinish) continue;

      // Check if it's an overlay type:
      // - type=4, OR
      // - _explicitType contains "OverlayFinishInfo"
      const isOverlayType = Number(selectedFinish.type) === 4;
      const isOverlayExplicitType = (selectedFinish as any)._explicitType?.includes('OverlayFinishInfo');

      console.log('[EditorStateService] isOverlayType:', isOverlayType, 'isOverlayExplicitType:', isOverlayExplicitType);

      if (isOverlayType || isOverlayExplicitType) {
        console.log('[EditorStateService] Found overlay finish:', selectedFinish);
        overlayFinishes.push({
          optionId: option.id,
          finishId: selectedFinish.id,
          image: selectedFinish.image
        });
      }
    }

    return overlayFinishes;
  }

  private async navigateToEditor(projectId?: number | string): Promise<void> {
    const queryParams = { ...(projectId ? { projectId } : {}), ...this.albumDesignParams() };
    await this.router.navigate(this.appState.buildLocaleCommands(['editor']), {
      queryParams
    });
    // One-shot context: consumed by this navigation.
    this._albumDesignContext.set(null);
  }

  /**
   * Navigate to editor in preview-only mode.
   * This opens the editor just to generate HQ previews, then auto-closes.
   * @param projectId - The album project ID
   * @param serverName - The project server name
   * @param coverUrls - Optional cover URLs (front and back) to include in preview upload
   * @param guardaImageUrl - Optional guarda (endpaper) texture image URL to tile as spread
   * @param transparentSheetPageUrl - Optional transparent sheet page thumbnail (with transparency already applied)
   */
  async navigateToEditorForPreviewGeneration(
    projectId: number | string,
    serverName: string,
    coverUrls?: { front: string; back: string },
    guardaImageUrl?: string,
    transparentSheetPageUrl?: string
  ): Promise<void> {
    // Store current URL for navigation back after preview generation
    // Only set if not already set (e.g., from transparent sheet capture flow)
    if (!this._previewReturnUrl()) {
      this.setPreviewReturnUrl(this.router.url);
    }

    // Store the serverName so editor can load the project
    this._existingProjectServerName.set(serverName);

    // Store cover URLs for editor to upload
    if (coverUrls) {
      this._coverUrlsForPreview.set(coverUrls);
    }

    // Store guarda image URL for tiled spread generation
    if (guardaImageUrl) {
      this._guardaImageUrl.set(guardaImageUrl);
      console.log('[EditorStateService] Set guarda image URL:', guardaImageUrl);
    }

    // Store transparent sheet page URL for spread generation
    if (transparentSheetPageUrl) {
      this._transparentSheetPageUrl.set(transparentSheetPageUrl);
      console.log('[EditorStateService] Set transparent sheet page URL');
    }

    await this.router.navigate(this.appState.buildLocaleCommands(['editor']), {
      queryParams: {
        projectId,
        generatePreviewsOnly: 'true'
      }
    });
  }

  /**
   * Clear cover URLs for preview (called after editor uses them).
   */
  clearCoverUrlsForPreview(): void {
    this._coverUrlsForPreview.set(null);
  }

  /**
   * Clear guarda image URL (called after editor uses it).
   */
  clearGuardaImageUrl(): void {
    this._guardaImageUrl.set(null);
  }

  /**
   * Set the URL to return to after preview generation completes.
   * This is stored before navigating to the editor and used when the editor closes.
   */
  setPreviewReturnUrl(url: string): void {
    console.log('[EditorStateService] Set preview return URL:', url);
    this._previewReturnUrl.set(url);
  }

  /**
   * Clear and get the return URL (called by editor when closing).
   * Returns the stored URL and clears the signal.
   */
  getAndClearPreviewReturnUrl(): string | null {
    const url = this._previewReturnUrl();
    this._previewReturnUrl.set(null);
    return url;
  }

  /**
   * Set transparent sheet page URL for preview generation.
   * The page will have white-to-transparent conversion applied.
   * @param pageUrl The transparent sheet page thumbnail URL/data URL
   */
  setTransparentSheetPageUrl(pageUrl: string): void {
    console.log('[EditorStateService] Set transparent sheet page URL');
    this._transparentSheetPageUrl.set(pageUrl);
  }

  /**
   * Clear transparent sheet page URL (called after editor uses it).
   */
  clearTransparentSheetPageUrl(): void {
    this._transparentSheetPageUrl.set(null);
  }

  /**
   * Set transparent sheet capture mode - when true, editor will capture snapshot and auto-close.
   */
  setTransparentSheetCaptureMode(enabled: boolean): void {
    console.log('[EditorStateService] Transparent sheet capture mode:', enabled);
    this._transparentSheetCaptureMode.set(enabled);
  }

  /**
   * Store captured transparent sheet snapshot from editor.
   */
  setCapturedTransparentSheetSnapshot(snapshot: string): void {
    console.log('[EditorStateService] Captured transparent sheet snapshot');
    this._capturedTransparentSheetSnapshot.set(snapshot);
  }

  /**
   * Clear captured transparent sheet snapshot.
   */
  clearCapturedTransparentSheetSnapshot(): void {
    this._capturedTransparentSheetSnapshot.set(null);
  }

  /**
   * Store album context for navigation back after transparent sheet capture.
   */
  setTransparentSheetAlbumContext(context: {
    albumProjectId: number;
    albumServerName: string;
    coverUrls?: { front: string; back: string };
    guardaImageUrl?: string;
  }): void {
    console.log('[EditorStateService] Set transparent sheet album context');
    this._transparentSheetAlbumContext.set(context);
  }

  /**
   * Clear album context after use.
   */
  clearTransparentSheetAlbumContext(): void {
    this._transparentSheetAlbumContext.set(null);
  }

  /**
   * Navigate to transparent sheet panel's editor to capture HQ snapshot.
   * After capture, the editor will auto-navigate back to the album editor.
   */
  async navigateToTransparentSheetEditorForCapture(
    panelProjectId: number,
    panelServerName: string,
    albumContext: {
      albumProjectId: number;
      albumServerName: string;
      coverUrls?: { front: string; back: string };
      guardaImageUrl?: string;
    }
  ): Promise<void> {
    // Store current URL for navigation back after the entire flow completes
    // This is set here (at the start of the flow) so we remember where to return to
    this.setPreviewReturnUrl(this.router.url);

    // Store album context for navigation back
    this.setTransparentSheetAlbumContext(albumContext);

    // Enable capture mode
    this.setTransparentSheetCaptureMode(true);

    // Store the serverName so editor can load the panel project (legacy storage)
    this._existingProjectServerName.set(panelServerName);

    console.log('[EditorStateService] Navigating to transparent sheet panel editor for capture');

    // Navigate to editor with projectId in URL (must use buildLocaleCommands for proper routing)
    await this.router.navigate(this.appState.buildLocaleCommands(['editor']), {
      queryParams: { projectId: panelProjectId }
    });
  }

  /**
   * Set album disposition (0 = single pages, 1 = double-page spreads).
   */
  setAlbumDisposition(disposition: number): void {
    this._albumDisposition.set(disposition);
  }

  /**
   * Set initial page photos for auto-population when creating a new album project.
   * These photos will be placed as backgrounds on each page.
   * @param photos Array of photoInfo objects from uploaded files (with sizes, serverName, etc.)
   */
  setInitialPageImages(photos: any[]): void {
    console.log('[EditorStateService] Setting initial page photos:', photos.length);
    this._initialPageImages.set(photos);
  }

  /**
   * Get and clear initial page photos (called by editor after consuming them).
   * Returns the photos and clears the signal.
   */
  getAndClearInitialPageImages(): any[] {
    const images = this._initialPageImages();
    this._initialPageImages.set([]);
    console.log('[EditorStateService] Getting and clearing initial page photos:', images.length);
    return images;
  }

  /**
   * Check if there are initial page images waiting to be consumed.
   */
  hasInitialPageImages(): boolean {
    return this._initialPageImages().length > 0;
  }

  /**
   * Request the editor to navigate through all pages after load to refresh thumbnails.
   * Used after cross-size import where source thumbnails have wrong dimensions.
   */
  requestRefreshAllThumbnails(): void {
    this._refreshAllThumbnails.set(true);
  }

  /**
   * Consume the refresh-all-thumbnails flag. Returns true if it was set.
   */
  consumeRefreshAllThumbnails(): boolean {
    const val = this._refreshAllThumbnails();
    if (val) this._refreshAllThumbnails.set(false);
    return val;
  }

  /**
   * Set the conversion progress dialog reference.
   * Called by products-list when opening the progress dialog.
   */
  setConversionProgressDialog(dialogRef: any): void {
    console.log('[EditorStateService] Setting conversion progress dialog');
    this._conversionProgressDialog.set(dialogRef);
  }

  /**
   * Get and clear conversion progress dialog reference.
   * Called by editor to update/close the dialog.
   */
  getAndClearConversionProgressDialog(): any {
    const dialogRef = this._conversionProgressDialog();
    this._conversionProgressDialog.set(null);
    return dialogRef;
  }

  /**
   * Check if there's a conversion progress dialog active.
   */
  hasConversionProgressDialog(): boolean {
    return this._conversionProgressDialog() !== null;
  }

  // ============================================
  // Bind=0 Cover Capture Mode Methods
  // ============================================

  /**
   * Set bind=0 cover capture mode.
   * When true, editor will capture HQ snapshots of the cover and auto-close.
   */
  setBindZeroCoverCaptureMode(enabled: boolean): void {
    console.log('[EditorStateService] Bind=0 cover capture mode:', enabled);
    this._bindZeroCoverCaptureMode.set(enabled);
  }

  /**
   * Store captured bind=0 cover snapshots (front and back halves).
   */
  setCapturedBindZeroCoverSnapshots(snapshots: { front: string; back: string }): void {
    console.log('[EditorStateService] Captured bind=0 cover snapshots');
    this._capturedBindZeroCoverSnapshots.set(snapshots);
  }

  /**
   * Clear captured bind=0 cover snapshots.
   */
  clearCapturedBindZeroCoverSnapshots(): void {
    this._capturedBindZeroCoverSnapshots.set(null);
  }

  /**
   * Set bind=0 cover capture context with layout info for cropping.
   */
  setBindZeroCoverCaptureContext(context: {
    layout: {
      flapTopPercent: number;
      flapBottomPercent: number;
      flapLeftPercent: number;
      flapRightPercent: number;
      spineWidthPercent: number;
      pageWidthPercent: number;
    };
    returnToFlipbook: boolean;
    albumProjectId?: number;
    albumServerName?: string;
  }): void {
    console.log('[EditorStateService] Set bind=0 cover capture context');
    this._bindZeroCoverCaptureContext.set(context);
  }

  /**
   * Clear bind=0 cover capture context.
   */
  clearBindZeroCoverCaptureContext(): void {
    this._bindZeroCoverCaptureContext.set(null);
  }

  // ============================================
  // Standard Cover Capture Mode Methods
  // ============================================

  /**
   * Set standard cover capture mode.
   * When true, editor will capture HQ snapshot and auto-close.
   */
  setStandardCoverCaptureMode(enabled: boolean): void {
    console.log('[EditorStateService] Standard cover capture mode:', enabled);
    this._standardCoverCaptureMode.set(enabled);
  }

  /**
   * Set standard cover capture context.
   */
  setStandardCoverCaptureContext(context: {
    isFrontCover: boolean;
    returnToFlipbook: boolean;
    albumProjectId?: number;
    albumServerName?: string;
    finishImageUrl?: string;
    overlayImageUrls?: string[];
  } | null): void {
    console.log('[EditorStateService] Set standard cover capture context:', context);
    this._standardCoverCaptureContext.set(context);
  }

  /**
   * Clear standard cover capture context.
   */
  clearStandardCoverCaptureContext(): void {
    this._standardCoverCaptureContext.set(null);
  }

  /**
   * Store captured standard cover front snapshot.
   * Reuses the bind=0 snapshots signal for storage.
   */
  setCapturedStandardCoverFront(snapshot: string): void {
    console.log('[EditorStateService] Storing standard cover front snapshot:', {
      length: snapshot?.length || 0,
      prefix: snapshot?.substring(0, 80) || 'null',
      isDataUrl: snapshot?.startsWith('data:') || false
    });
    const existing = this._capturedBindZeroCoverSnapshots();
    this._capturedBindZeroCoverSnapshots.set({
      front: snapshot,
      back: existing?.back || ''
    });
    // Verify storage
    const stored = this._capturedBindZeroCoverSnapshots();
    console.log('[EditorStateService] Verified stored standard cover front:', {
      storedLength: stored?.front?.length || 0,
      storedPrefix: stored?.front?.substring(0, 80) || 'null'
    });
  }

  /**
   * Store captured standard cover back snapshot.
   */
  setCapturedStandardCoverBack(snapshot: string): void {
    console.log('[EditorStateService] Captured standard cover back snapshot');
    const existing = this._capturedBindZeroCoverSnapshots();
    this._capturedBindZeroCoverSnapshots.set({
      front: existing?.front || '',
      back: snapshot
    });
  }

  /**
   * Navigate to cover editor to capture high-quality bind=0 cover snapshots.
   * After capture, returns to flipbook preview with the captured images.
   */
  async navigateToBindZeroCoverEditorForCapture(
    coverProjectId: number,
    coverServerName: string,
    layout: {
      flapTopPercent: number;
      flapBottomPercent: number;
      flapLeftPercent: number;
      flapRightPercent: number;
      spineWidthPercent: number;
      pageWidthPercent: number;
    },
    albumProjectId?: number,
    albumServerName?: string
  ): Promise<void> {
    // Store current URL for navigation back
    this.setPreviewReturnUrl(this.router.url);

    // Store capture context
    this.setBindZeroCoverCaptureContext({
      layout,
      returnToFlipbook: true,
      albumProjectId,
      albumServerName
    });

    // Enable capture mode
    this.setBindZeroCoverCaptureMode(true);

    // Store the serverName so editor can load the cover project (legacy storage)
    this._existingProjectServerName.set(coverServerName);

    console.log('[EditorStateService] Navigating to bind=0 cover editor for capture');

    // Navigate to editor with projectId in URL
    await this.router.navigate(this.appState.buildLocaleCommands(['editor']), {
      queryParams: { projectId: coverProjectId }
    });
  }

  // ============================================
  // Flipbook Capture Chain Methods
  // ============================================

  /**
   * Start the flipbook capture chain.
   * This sets up all the steps needed and initiates the first capture.
   */
  startFlipbookCaptureChain(config: {
    returnUrl: string;
    // Standard cover capture (non-bind=0) - captures front then back with bleed cropping
    standardCoverCapture?: {
      projectId: number;
      serverName: string;
      productId?: number;       // Cover product ID for API calls
      editorTypeId?: number;    // Editor type (COVERS=3) for finish priority in loadProductConfig
      finishImageUrl?: string;  // Finish/Revestimento image to use as background
      overlayImageUrls?: string[]; // Additional overlay images (e.g., lomba) to apply on top
    };
    // Bind=0 cover capture (optional)
    bindZeroCoverCapture?: {
      projectId: number;
      serverName: string;
      layout: {
        flapTopPercent: number;
        flapBottomPercent: number;
        flapLeftPercent: number;
        flapRightPercent: number;
        spineWidthPercent: number;
        pageWidthPercent: number;
      };
      revestimentoFrontUrl?: string;
      revestimentoBackUrl?: string;
      designPosition?: { left: number; top: number; width: number; height: number };
    };
    // Transparent sheet capture (optional)
    transparentSheetCapture?: {
      projectId: number;
      serverName: string;
    };
    // Album editor for page previews (always required)
    albumProjectId: number;
    albumServerName: string;
    coverUrls?: { front: string; back: string };
    guardaImageUrl?: string;
  }): void {
    console.log('[EditorStateService] Starting flipbook capture chain:', {
      needsStandardCover: !!config.standardCoverCapture,
      needsBindZeroCover: !!config.bindZeroCoverCapture,
      needsTransparentSheet: !!config.transparentSheetCapture,
      albumProjectId: config.albumProjectId
    });

    this._flipbookCaptureChain.set({
      active: true,
      returnUrl: config.returnUrl,
      // Standard cover (non-bind=0)
      needsStandardCoverCapture: !!config.standardCoverCapture,
      standardCoverProjectId: config.standardCoverCapture?.projectId,
      standardCoverServerName: config.standardCoverCapture?.serverName,
      standardCoverProductId: config.standardCoverCapture?.productId,
      standardCoverEditorTypeId: config.standardCoverCapture?.editorTypeId,
      standardCoverFinishImageUrl: config.standardCoverCapture?.finishImageUrl,
      standardCoverOverlayImageUrls: config.standardCoverCapture?.overlayImageUrls,
      standardCoverCapturedFront: false,
      // Bind=0 cover
      needsBindZeroCoverCapture: !!config.bindZeroCoverCapture,
      bindZeroCoverProjectId: config.bindZeroCoverCapture?.projectId,
      bindZeroCoverServerName: config.bindZeroCoverCapture?.serverName,
      bindZeroCoverLayout: config.bindZeroCoverCapture?.layout,
      bindZeroCoverRevestimentoFrontUrl: config.bindZeroCoverCapture?.revestimentoFrontUrl,
      bindZeroCoverRevestimentoBackUrl: config.bindZeroCoverCapture?.revestimentoBackUrl,
      bindZeroCoverDesignPosition: config.bindZeroCoverCapture?.designPosition,
      // Transparent sheet
      needsTransparentSheetCapture: !!config.transparentSheetCapture,
      transparentSheetProjectId: config.transparentSheetCapture?.projectId,
      transparentSheetServerName: config.transparentSheetCapture?.serverName,
      // Album
      albumProjectId: config.albumProjectId,
      albumServerName: config.albumServerName,
      coverUrls: config.coverUrls,
      guardaImageUrl: config.guardaImageUrl
    });

    // Start the loading overlay with the first step
    const firstStep = config.standardCoverCapture ? 'cover'
      : config.bindZeroCoverCapture ? 'cover'
      : config.transparentSheetCapture ? 'transparent_sheet'
      : 'book_block';
    this.startCaptureChainLoading(firstStep);
  }

  /**
   * Mark bind=0 cover capture as completed and advance chain.
   */
  completeBindZeroCoverCapture(): void {
    const chain = this._flipbookCaptureChain();
    if (!chain) return;

    console.log('[EditorStateService] Bind=0 cover capture completed, advancing chain');
    this._flipbookCaptureChain.set({
      ...chain,
      needsBindZeroCoverCapture: false
    });
  }

  /**
   * Mark transparent sheet capture as completed and advance chain.
   */
  completeTransparentSheetCapture(): void {
    const chain = this._flipbookCaptureChain();
    if (!chain) return;

    console.log('[EditorStateService] Transparent sheet capture completed, advancing chain');
    this._flipbookCaptureChain.set({
      ...chain,
      needsTransparentSheetCapture: false
    });
  }

  /**
   * Mark standard cover front capture as done, preparing for back capture.
   */
  completeStandardCoverFrontCapture(): void {
    const chain = this._flipbookCaptureChain();
    if (!chain) return;

    console.log('[EditorStateService] Standard cover front captured, will capture back next');
    this._flipbookCaptureChain.set({
      ...chain,
      standardCoverCapturedFront: true
    });
  }

  /**
   * Mark standard cover capture as fully completed (both front and back).
   */
  completeStandardCoverCapture(): void {
    const chain = this._flipbookCaptureChain();
    if (!chain) return;

    console.log('[EditorStateService] Standard cover capture completed, advancing chain');
    this._flipbookCaptureChain.set({
      ...chain,
      needsStandardCoverCapture: false
    });
  }

  /**
   * Get the next step in the capture chain.
   * Returns the step to execute or 'complete' if all captures are done.
   */
  getNextCaptureChainStep(): 'standard_cover' | 'bind_zero_cover' | 'transparent_sheet' | 'album_editor' | 'complete' | null {
    const chain = this._flipbookCaptureChain();
    if (!chain || !chain.active) return null;

    // Standard cover first (captures front then back)
    if (chain.needsStandardCoverCapture) {
      return 'standard_cover';
    }
    if (chain.needsBindZeroCoverCapture) {
      return 'bind_zero_cover';
    }
    if (chain.needsTransparentSheetCapture) {
      return 'transparent_sheet';
    }
    // All captures done, go to album editor
    return 'album_editor';
  }

  /**
   * Execute the next step in the capture chain.
   * Returns true if navigation was triggered, false if chain is complete.
   */
  async executeNextCaptureChainStep(): Promise<boolean> {
    const chain = this._flipbookCaptureChain();
    const nextStep = this.getNextCaptureChainStep();

    if (!chain || !nextStep) {
      console.log('[EditorStateService] No capture chain active');
      return false;
    }

    console.log('[EditorStateService] Executing next capture chain step:', nextStep);

    switch (nextStep) {
      case 'standard_cover':
        if (chain.standardCoverProjectId && chain.standardCoverServerName) {
          // Update loading step
          this.updateCaptureChainLoadingStep('cover');

          // Determine if we need front or back capture
          const isFrontCapture = !chain.standardCoverCapturedFront;

          // Set up standard cover capture context
          this.setStandardCoverCaptureContext({
            isFrontCover: isFrontCapture,
            returnToFlipbook: true,
            albumProjectId: chain.albumProjectId,
            albumServerName: chain.albumServerName,
            finishImageUrl: chain.standardCoverFinishImageUrl,
            overlayImageUrls: chain.standardCoverOverlayImageUrls
          });
          this.setStandardCoverCaptureMode(true);
          this._existingProjectServerName.set(chain.standardCoverServerName);

          // CRITICAL: Set product info so loadProductConfig knows this is a COVERS editor
          // Without this, editorTypeId defaults to GIFTS and uses wrong finish priority
          if (chain.standardCoverProductId || chain.standardCoverEditorTypeId) {
            const minimalProductInfo = {
              id: chain.standardCoverProductId || 0,
              editorTypeId: chain.standardCoverEditorTypeId || 3, // Default to COVERS type
              typeId: 0,
              name: this.translate.instant('PRODUCTS.COVER'),
              ref: '',
              qty: 1,
              stepQty: 1,
              minQty: 1,
              showcase: 0
            } as any;
            this._product.set(minimalProductInfo);
            console.log('[EditorStateService] Set product info for standard cover capture:', {
              productId: minimalProductInfo.id,
              editorTypeId: minimalProductInfo.editorTypeId
            });
          }

          // Navigate to cover editor
          await this.router.navigate(this.appState.buildLocaleCommands(['editor']), {
            queryParams: { projectId: chain.standardCoverProjectId }
          });
          return true;
        }
        // Can't execute, skip to next
        this.completeStandardCoverCapture();
        return this.executeNextCaptureChainStep();

      case 'bind_zero_cover':
        if (chain.bindZeroCoverProjectId && chain.bindZeroCoverServerName && chain.bindZeroCoverLayout) {
          // Update loading step
          this.updateCaptureChainLoadingStep('cover');

          // Set up bind=0 cover capture context
          this.setBindZeroCoverCaptureContext({
            layout: chain.bindZeroCoverLayout,
            returnToFlipbook: true,
            albumProjectId: chain.albumProjectId,
            albumServerName: chain.albumServerName
          });
          this.setBindZeroCoverCaptureMode(true);
          this._existingProjectServerName.set(chain.bindZeroCoverServerName);

          // Navigate to cover editor
          await this.router.navigate(this.appState.buildLocaleCommands(['editor']), {
            queryParams: { projectId: chain.bindZeroCoverProjectId }
          });
          return true;
        }
        // Can't execute, skip to next
        this.completeBindZeroCoverCapture();
        return this.executeNextCaptureChainStep();

      case 'transparent_sheet':
        if (chain.transparentSheetProjectId && chain.transparentSheetServerName) {
          // Update loading step
          this.updateCaptureChainLoadingStep('transparent_sheet');

          // Set up transparent sheet capture context
          this._transparentSheetAlbumContext.set({
            albumProjectId: chain.albumProjectId,
            albumServerName: chain.albumServerName,
            coverUrls: chain.coverUrls,
            guardaImageUrl: chain.guardaImageUrl
          });
          this.setTransparentSheetCaptureMode(true);
          this._existingProjectServerName.set(chain.transparentSheetServerName);

          // Navigate to transparent sheet editor
          await this.router.navigate(this.appState.buildLocaleCommands(['editor']), {
            queryParams: { projectId: chain.transparentSheetProjectId }
          });
          return true;
        }
        // Can't execute, skip to next
        this.completeTransparentSheetCapture();
        return this.executeNextCaptureChainStep();

      case 'album_editor':
        // Update loading step
        this.updateCaptureChainLoadingStep('book_block');

        // Navigate to album editor for page preview generation
        // Clear the chain now - album editor will handle the rest
        const albumProjectId = chain.albumProjectId;
        const albumServerName = chain.albumServerName;
        const coverUrls = chain.coverUrls;
        const guardaImageUrl = chain.guardaImageUrl;
        const returnUrl = chain.returnUrl;

        // Store return URL for after preview generation
        this.setPreviewReturnUrl(returnUrl);

        // Navigate to album editor with preview generation mode
        await this.navigateToEditorForPreviewGeneration(
          albumProjectId,
          albumServerName,
          coverUrls,
          guardaImageUrl,
          undefined // transparent sheet snapshot is already captured
        );
        return true;

      default:
        return false;
    }
  }

  /**
   * Clear the flipbook capture chain.
   */
  clearFlipbookCaptureChain(): void {
    console.log('[EditorStateService] Clearing flipbook capture chain');
    this._flipbookCaptureChain.set(null);
  }

  /**
   * Check if a capture chain is active.
   */
  hasActiveCaptureChain(): boolean {
    const chain = this._flipbookCaptureChain();
    return chain !== null && chain.active;
  }

  /**
   * Start the capture chain loading overlay.
   * This shows a full-screen loading overlay that persists across component navigation.
   */
  startCaptureChainLoading(step: 'cover' | 'transparent_sheet' | 'book_block' | 'uploading'): void {
    const messages = {
      cover: this.translate.instant('UI_TEXT.CAPTURE_COVER'),
      transparent_sheet: this.translate.instant('UI_TEXT.CAPTURE_TRANSPARENT_SHEET'),
      book_block: this.translate.instant('UI_TEXT.CAPTURE_BOOK_BLOCK'),
      uploading: this.translate.instant('UI_TEXT.CAPTURE_UPLOADING')
    };

    console.log('[EditorStateService] Starting capture chain loading:', step);
    this._captureChainLoading.set({
      active: true,
      message: messages[step],
      currentStep: step
    });
  }

  /**
   * Update the capture chain loading step.
   */
  updateCaptureChainLoadingStep(step: 'cover' | 'transparent_sheet' | 'book_block' | 'uploading'): void {
    const current = this._captureChainLoading();
    if (!current?.active) return;

    const messages = {
      cover: this.translate.instant('UI_TEXT.CAPTURE_COVER'),
      transparent_sheet: this.translate.instant('UI_TEXT.CAPTURE_TRANSPARENT_SHEET'),
      book_block: this.translate.instant('UI_TEXT.CAPTURE_BOOK_BLOCK'),
      uploading: this.translate.instant('UI_TEXT.CAPTURE_UPLOADING')
    };

    console.log('[EditorStateService] Updating capture chain loading step:', step);
    this._captureChainLoading.set({
      active: true,
      message: messages[step],
      currentStep: step
    });
  }

  /**
   * Update the capture chain loading progress (current/total) and optionally the message.
   */
  updateCaptureChainProgress(current: number, total: number, message?: string): void {
    const loading = this._captureChainLoading();
    if (!loading?.active) return;

    this._captureChainLoading.set({
      ...loading,
      ...(message ? { message } : {}),
      progress: { current, total }
    });
  }

  /**
   * Stop the capture chain loading overlay.
   */
  stopCaptureChainLoading(): void {
    console.log('[EditorStateService] Stopping capture chain loading');
    this._captureChainLoading.set(null);
  }

  /**
   * Check if capture chain loading is active.
   */
  isCaptureChainLoading(): boolean {
    const loading = this._captureChainLoading();
    return loading !== null && loading.active;
  }

  /**
   * Stop the editor opening overlay.
   * Called by EditorComponent after loadFromRoute() completes.
   */
  stopEditorLoading(): void {
    this._editorOpening.set(false);
  }
}
